import { useRef } from "react";
import type { ReactNode } from "react";
import LeafRectangleLoader from "../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import LeafTabViewHeader from "./LeafTabViewHeader/LeafTabViewHeader";
import "./LeafTabViews.scss";

export type TabView = {
  id?: string;
  title?: string;
  View?: ReactNode;
  disabled?: boolean;
};

export type Props = {
  Views: TabView[];
  setViewOnClick: Function;
  activeView: string;
  isLoading?: boolean;
  buttonify?: boolean;
  headerRight?: ReactNode;
};

const LeafTabViews = (props: Props) => {
  const { Views, setViewOnClick, activeView, isLoading = false } = props;
  const tabViewRef = useRef<HTMLDivElement>(null);
  const tabContentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const tabView = tabViewRef.current;
    const tabElements = tabView?.querySelectorAll<HTMLElement>('[role="tab"]');
    const arrayFromTabElements = Array.from(tabElements || []);
    const lastIndex = arrayFromTabElements.length - 1;
    const targetId = (event.target as HTMLElement).id;
    if (event.key === "ArrowLeft" && tabElements) {
      const index = arrayFromTabElements.findIndex((element) => element.id === targetId);
      index === 0 ? tabElements[lastIndex].focus() : tabElements[index - 1].focus();
    }
    if (event.key === "ArrowRight" && tabElements) {
      const index = arrayFromTabElements.findIndex((element) => element.id === targetId);
      index === lastIndex ? tabElements[0].focus() : tabElements[index + 1].focus();
    }
    // Focus on the tab content when down arrow is pressed
    if (event.key === "ArrowDown" && tabView) {
      const index = arrayFromTabElements.findIndex((element) => element.id === targetId);
      const activeContent = tabContentRefs.current[index];
      if (activeContent) {
        // Find the first focusable element within the active content
        const firstFocusableElement = activeContent.querySelector<HTMLElement>(
          'a, button, input, select, textarea,[tabindex]:not([tabindex="-1"])',
        );
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        } else {
          // Focus the active content itself if no focusable element is found
          activeContent.focus();
        }
      }
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard navigation handler for tab switching
    <div className="leaf-tab-views" onKeyDown={handleKeyDown} ref={tabViewRef}>
      {isLoading ? (
        <div className="leaf-tab-views__loader">
          <LeafRectangleLoader isLoading={true} width="18.75rem" height="1.5rem" />
        </div>
      ) : (
        <>
          <div className="leaf-tab-views__header-container">
            <div
              className="leaf-tab-views__header"
              role="tablist"
              style={{ gap: props.buttonify ? ".5rem" : "", marginTop: props.buttonify ? "0" : "" }}
            >
              {Views.map(({ id, title, disabled = false }) => (
                <LeafTabViewHeader
                  tabItem={{ id, title, disabled, activeView }}
                  key={id}
                  tabHeaderOnClick={setViewOnClick}
                  buttonify={props.buttonify}
                />
              ))}
            </div>
            {props.headerRight}
          </div>

          <div className="leaf-tab-views__content">
            {Views.map(({ id, View }, index) => (
              <div
                className={`leaf-tab-views__item${id === activeView ? "--active" : ""}`}
                key={id}
                role="tabpanel"
                id={`tabpanel-${id}`}
                ref={(el) => {
                  tabContentRefs.current[index] = el;
                }}
                tabIndex={id === activeView ? 0 : -1} // Make the active tab content focusable
              >
                {id === activeView ? View : ""}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LeafTabViews;
